import { ThemedText } from "@/components/themed-text";
import { BrandColors } from "@/constants/theme";
import { useAuth } from "@/contexts/auth-context";
import {
  SERVICE_SECTIONS,
  type ServiceIcon,
  type ServiceTypeOption,
} from "@/data/service-types";
import {
  useDesktopLayout,
  useServiceSearchQuery,
} from "@/hooks/use-desktop-layout";
import { useColorScheme } from "@/hooks/use-color-scheme";
import { matchesServiceQuery } from "@/lib/desktop-layout";
import { ServiceTypeValue } from "@/lib/validation/booking";
import { useCreateRequestStore } from "@/stores/use-create-request-store";
import Ionicons from "@react-native-vector-icons/ionicons";
import MaterialCommunityIcons from "@react-native-vector-icons/material-design-icons";
import { router, useFocusEffect } from "expo-router";
import React, { useCallback, useRef, useState } from "react";
import { StyleSheet, TouchableOpacity, View } from "react-native";

function ServiceIconView({ icon, size = 28 }: { icon: ServiceIcon; size?: number }) {
  const color = BrandColors.primary;

  if (icon.family === "material") {
    return (
      <MaterialCommunityIcons name={icon.name as any} size={size} color={color} />
    );
  }

  return <Ionicons name={icon.name as any} size={size} color={color} />;
}

function DesktopServiceCard({
  service,
  onPress,
  cardColor,
  iconWellColor,
  chevronColor,
  disabled,
}: {
  service: ServiceTypeOption;
  onPress: () => void;
  cardColor: string;
  iconWellColor: string;
  chevronColor: string;
  disabled: boolean;
}) {
  return (
    <TouchableOpacity
      style={[styles.desktopCard, { backgroundColor: cardColor }, disabled && styles.cardDisabled]}
      onPress={onPress}
      disabled={disabled}
      activeOpacity={0.75}
      accessibilityRole="button"
      accessibilityLabel={service.label}
      accessibilityState={{ disabled }}
    >
      <View style={[styles.iconWell, { backgroundColor: iconWellColor }]}>
        <ServiceIconView icon={service.icon} size={26} />
      </View>
      <View style={styles.desktopCopy}>
        <ThemedText style={styles.desktopTitle} type="defaultSemiBold">
          {service.label ?? service.shortLabel}
        </ThemedText>
        {service.description ? (
          <ThemedText style={styles.desktopDescription} type="small">
            {service.description}
          </ThemedText>
        ) : null}
      </View>
      <View style={[styles.chevronWell, { backgroundColor: chevronColor }]}>
        <Ionicons name="chevron-forward" size={16} color={BrandColors.mediumGray} />
      </View>
    </TouchableOpacity>
  );
}

function ServiceCard({
  service,
  onPress,
  columns,
  cardColor,
  disabled,
}: {
  service: ServiceTypeOption;
  onPress: () => void;
  columns: 2 | 3;
  cardColor: string;
  disabled: boolean;
}) {
  return (
    <TouchableOpacity
      style={[
        styles.card,
        {
          backgroundColor: cardColor,
          flexBasis: columns === 3 ? "30%" : "47%",
        },
        disabled && styles.cardDisabled,
      ]}
      onPress={onPress}
      disabled={disabled}
      activeOpacity={0.75}
      accessibilityRole="button"
      accessibilityLabel={service.label}
      accessibilityState={{ disabled }}
    >
      <ServiceIconView icon={service.icon} />
      <ThemedText style={styles.cardLabel} type="small">
        {service.label}
      </ThemedText>
    </TouchableOpacity>
  );
}

export function ServiceRegistration({ route = true }: { route?: boolean }) {
  const { theme } = useAuth();
  const colorScheme = useColorScheme();
  const desktop = useDesktopLayout();
  const searchQuery = useServiceSearchQuery();
  const { setFormData, setCurrentStep } = useCreateRequestStore();
  const selectingRef = useRef(false);
  const [selectedService, setSelectedService] = useState<ServiceTypeValue | null>(
    null,
  );

  useFocusEffect(
    useCallback(() => {
      selectingRef.current = false;
      setSelectedService(null);
    }, []),
  );

  const handleSelect = useCallback(
    (value: ServiceTypeValue) => {
      if (selectingRef.current) {
        return;
      }

      selectingRef.current = true;
      setSelectedService(value);
      setFormData({ service_type: value, questions: [] });
      setCurrentStep(1);
      if (route) {
        router.push("/customer/create-request/step1");
      }
    },
    [setFormData, setCurrentStep, route],
  );

  const sections = SERVICE_SECTIONS.map((section) => ({
    ...section,
    services: desktop
      ? section.services.filter((service) =>
          matchesServiceQuery(service, searchQuery),
        )
      : section.services,
  })).filter((section) => section.services.length > 0);

  return (
    <View style={styles.container}>
      {sections.map((section) => (
        <View key={section.title} style={styles.section}>
          <ThemedText
            style={[styles.sectionTitle, desktop && styles.desktopSectionTitle]}
            type="defaultSemiBold"
          >
            {section.title}
          </ThemedText>
          <View style={[styles.grid, desktop && styles.desktopGrid]}>
            {section.services.map((service) =>
              desktop ? (
                <DesktopServiceCard
                  key={service.value}
                  service={service}
                  cardColor={colorScheme === "dark" ? "#1B2436" : BrandColors.white}
                  iconWellColor="rgba(15, 185, 177, 0.12)"
                  chevronColor={theme.border}
                  disabled={
                    selectedService !== null && selectedService !== service.value
                  }
                  onPress={() => handleSelect(service.value as ServiceTypeValue)}
                />
              ) : (
                <ServiceCard
                  key={service.value}
                  service={service}
                  columns={section.columns}
                  cardColor={theme.card}
                  disabled={
                    selectedService !== null && selectedService !== service.value
                  }
                  onPress={() => handleSelect(service.value as ServiceTypeValue)}
                />
              ),
            )}
          </View>
        </View>
      ))}
      {desktop && sections.length === 0 ? (
        <ThemedText style={styles.emptySearch} type="small">
          No services match your search.
        </ThemedText>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 20,
  },
  section: {
    gap: 12,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: "700",
  },
  grid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
  },
  card: {
    flexGrow: 1,
    minHeight: 100,
    paddingHorizontal: 8,
    paddingVertical: 14,
    borderRadius: 14,
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    borderWidth: 0.4,
    borderColor: BrandColors.lightGray + "60",
  },
  cardDisabled: {
    opacity: 0.45,
  },
  cardLabel: {
    fontSize: 12,
    textAlign: "center",
    lineHeight: 22,
  },
  desktopSectionTitle: {
    fontSize: 20,
  },
  desktopGrid: {
    gap: 14,
  },
  desktopCard: {
    flexGrow: 1,
    flexBasis: "31%",
    maxWidth: "32.5%",
    minHeight: 104,
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    paddingHorizontal: 16,
    paddingVertical: 16,
    borderRadius: 16,
    shadowColor: "#0D1B2E",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.05,
    shadowRadius: 9,
    elevation: 2,
  },
  iconWell: {
    width: 56,
    height: 56,
    borderRadius: 16,
    alignItems: "center",
    justifyContent: "center",
  },
  desktopCopy: {
    flex: 1,
    gap: 4,
  },
  desktopTitle: {
    fontSize: 15,
  },
  desktopDescription: {
    lineHeight: 18,
  },
  chevronWell: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: "center",
    justifyContent: "center",
  },
  emptySearch: {
    color: BrandColors.darkGray,
  },
});
